import { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, BookOpen, FileText, GraduationCap, MapPin, Clock } from 'lucide-react'

import { cn } from '@/lib/utils'
import { api, AppLimitError } from '@/lib/api'
import { useUpgradeStore } from '@/stores/upgrade.store'

// ---------------------------------------------------------------------------
// Types (defined locally — richer than the global ProgramTemplate model)
// ---------------------------------------------------------------------------

/**
 * Essay requirement within a program template.
 */
export interface ProgramEssay {
  prompt: string
  wordLimit?: number
}

/**
 * Deadline entry within a program template (programs may have multiple rounds).
 */
export interface ProgramDeadline {
  round?: string
  date: string
}

/**
 * Full program template as returned by GET /api/v1/program-templates.
 *
 * Fields beyond the global ProgramTemplate model are optional — the backend
 * may not populate all of them for every record.
 */
export interface ProgramTemplate {
  id: string
  universityName: string
  programName: string
  degree?: string
  country?: string
  deadline?: string
  deadlines?: ProgramDeadline[]
  essays?: ProgramEssay[]
  essayCount?: number
  lorCount?: number
  gmatAccepted?: boolean
  greAccepted?: boolean
  testRequired?: string[]
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ProgramDetailModalProps {
  program: ProgramTemplate | null
  isOpen: boolean
  onClose: () => void
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/**
 * Returns a country flag emoji for a given country name.
 * Falls back to a globe emoji for unrecognised countries.
 */
function countryFlag(country?: string): string {
  if (!country) return '🌐'
  const map: Record<string, string> = {
    'United States': '🇺🇸',
    'USA': '🇺🇸',
    'United Kingdom': '🇬🇧',
    'UK': '🇬🇧',
    'Canada': '🇨🇦',
    'France': '🇫🇷',
    'Germany': '🇩🇪',
    'Spain': '🇪🇸',
    'Singapore': '🇸🇬',
    'India': '🇮🇳',
    'Australia': '🇦🇺',
    'Switzerland': '🇨🇭',
  }
  return map[country] ?? '🌐'
}

// ---------------------------------------------------------------------------
// ProgramDetailModal
// ---------------------------------------------------------------------------

/**
 * ProgramDetailModal — shows full details for a selected MBA program template.
 *
 * The "Add to Applications" button posts to the backend. If the user has hit
 * their application limit, the UpgradeModal is opened instead of an error toast.
 */
export default function ProgramDetailModal({
  program,
  isOpen,
  onClose,
}: ProgramDetailModalProps) {
  const queryClient = useQueryClient()
  const openUpgrade = useUpgradeStore((s) => s.open)
  const [addError, setAddError] = useState<string | null>(null)
  const [addSuccess, setAddSuccess] = useState(false)

  const addMutation = useMutation({
    mutationFn: (programTemplateId: string) =>
      api.post('/v1/applications', { programTemplateId }),
    onSuccess: () => {
      // Invalidate applications list so Kanban board reflects the new entry
      queryClient.invalidateQueries({ queryKey: ['applications'] })
      setAddSuccess(true)
      setAddError(null)
    },
    onError: (err: unknown) => {
      if (err instanceof AppLimitError) {
        onClose()
        openUpgrade()
        return
      }
      setAddError(err instanceof Error ? err.message : 'Failed to add application.')
    },
  })

  function handleClose() {
    onClose()
    // Reset state after dialog animation completes
    setTimeout(() => {
      setAddError(null)
      setAddSuccess(false)
    }, 200)
  }

  function handleAdd() {
    if (!program) return
    setAddError(null)
    setAddSuccess(false)
    addMutation.mutate(program.id)
  }

  if (!program) return null

  // Derive essay list — prefer the detailed array, fall back to count-only display
  const essays = program.essays ?? []
  const essayCount = program.essayCount ?? essays.length

  // Derive deadline list
  const deadlines = program.deadlines ?? (program.deadline ? [{ date: program.deadline }] : [])

  // Derive test requirements
  const testRequired = program.testRequired ?? [
    ...(program.gmatAccepted ? ['GMAT'] : []),
    ...(program.greAccepted ? ['GRE'] : []),
  ]

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2',
            'max-h-[90vh] overflow-y-auto rounded-xl border bg-background p-6 shadow-xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Dialog.Title className="text-xl font-semibold text-foreground leading-tight">
                {program.universityName}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-muted-foreground">
                {program.programName}
                {program.degree && (
                  <span className="ml-2 inline-flex items-center rounded-full border border-brand-blue/30 bg-brand-blue/10 px-2 py-0.5 text-xs font-medium text-brand-blue">
                    {program.degree}
                  </span>
                )}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring flex-shrink-0"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Quick stats row */}
          <div className="mt-5 flex flex-wrap gap-3">
            {program.country && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm text-foreground">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                {countryFlag(program.country)} {program.country}
              </span>
            )}
            {program.lorCount !== undefined && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm text-foreground">
                <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                {program.lorCount} LOR{program.lorCount !== 1 ? 's' : ''}
              </span>
            )}
            {essayCount > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm text-foreground">
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                {essayCount} Essay{essayCount !== 1 ? 's' : ''}
              </span>
            )}
            {testRequired.length > 0 && testRequired.map((test) => (
              <span
                key={test}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-purple/10 px-3 py-1 text-sm font-medium text-brand-purple"
              >
                <GraduationCap className="h-3.5 w-3.5" />
                {test}
              </span>
            ))}
          </div>

          {/* Deadlines */}
          {deadlines.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Deadlines
              </h3>
              <div className="space-y-2">
                {deadlines.map((d, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-2.5 text-sm"
                  >
                    <span className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      {d.round ?? 'Deadline'}
                    </span>
                    <span className="font-medium text-foreground">
                      {new Date(d.date).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Essays */}
          {essays.length > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Essays
              </h3>
              <div className="space-y-3">
                {essays.map((essay, i) => (
                  <div key={i} className="rounded-lg border bg-muted/30 p-3">
                    <p className="text-sm text-foreground leading-relaxed">{essay.prompt}</p>
                    {essay.wordLimit && (
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        Word limit: {essay.wordLimit.toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Essay count only (when no detailed list) */}
          {essays.length === 0 && essayCount > 0 && (
            <div className="mt-6">
              <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Essays
              </h3>
              <p className="text-sm text-muted-foreground">
                {essayCount} essay{essayCount !== 1 ? 's' : ''} required. Detailed prompts not yet available.
              </p>
            </div>
          )}

          {/* Feedback */}
          {addError && (
            <p className="mt-4 rounded-md bg-destructive/10 px-4 py-2 text-sm text-destructive">
              {addError}
            </p>
          )}
          {addSuccess && (
            <p className="mt-4 rounded-md bg-brand-green/10 px-4 py-2 text-sm text-brand-green font-medium">
              Added to your applications. View it on your Kanban board.
            </p>
          )}

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={addMutation.isPending || addSuccess}
              className="rounded-md bg-brand-blue px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {addMutation.isPending
                ? 'Adding...'
                : addSuccess
                ? 'Added!'
                : 'Add to Applications'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
